import { useLocalStorage } from '@vueuse/core'

import type { ListsStore, EnsureOptions } from './interfaces'
import type { Lists } from '../interfaces'

import { useListsApi } from '@shared/api'
import { parseStoredJson } from '@shared/lib/storage'

const STORAGE_KEY = 'lists'

const persist = (state: ListsStore): void => {
  const ls = useLocalStorage<Lists | null>(STORAGE_KEY, null, {
    serializer: {
      read: (v) => parseStoredJson<Lists>(v),
      write: (v) => (v ? JSON.stringify(v) : ''),
    },
  })
  const lists = toRaw(state.lists) as Lists | null

  ls.value = lists ? { ...lists } : null
}

const setLists = (state: ListsStore, lists: ListsStore['lists']) => {
  state.lists = toRaw(lists)
  persist(state)
}

const clear = (state: ListsStore): void => {
  state.lists = null
  persist(state)
}

export const actions = {
  /** Lists already in memory are fresher than the storage copy and are kept. */
  hydrateFromStorage (this: ListsStore): void {
    if (this._hydrated || this.lists) {
      this._hydrated = true
      return
    }

    const ls = useLocalStorage<Lists | null>(STORAGE_KEY, null, {
      serializer: {
        read: (v) => parseStoredJson<Lists>(v),
        write: (v) => (v ? JSON.stringify(v) : ''),
      },
    })

    this.lists = ls.value ? { ...ls.value } : null
    this._hydrated = true
  },

  setLists (this: ListsStore, lists: ListsStore['lists']): void {
    return setLists(this, lists)
  },

  async ensure (this: ListsStore, options: EnsureOptions = {}): Promise<ListsStore['lists']> {
    if (!options.force && this.lists) {
      return this.lists
    }

    if (this.lists && options.background) {
      void useListsApi()

      return this.lists
    }

    return await useListsApi()
  },

  persist (this: ListsStore): void {
    return persist(this)
  },

  async refreshNow (this: ListsStore): Promise<ListsStore['lists']> {
    return await useListsApi()
  },

  clear (this: ListsStore): void {
    return clear(this)
  },
}
